import axios from 'app/axios'
import { AggregationColor } from 'antd/es/color-picker/color'
import { FC, ReactNode, createContext, useContext, useEffect, useState } from 'react'

export type ThemeHexColors = {
  primaryColor: string
  secondaryColor: string
}

export type ThemeAggregatedColors = {
  primaryColor: AggregationColor
  secondaryColor: AggregationColor
}

export type ThemeColors = {
  primaryColor: string
  secondaryColor: string
}

export type ThemeContextType = {
  theme: ThemeAggregatedColors
  themeColors: ThemeColors
  workspaceLogo: string | null
  isLoading: boolean
  setThemeColors: (colors: ThemeColors) => void
  setTheme: (theme: ThemeAggregatedColors) => void
  setWorkspaceLogo: (logo: string | null) => void
}

// OT-1249: standardised Outcore brand palette (== --colorPrimary / --colorSecondary in
// index.css). Custom Workspace colours are no longer applied — the whole platform uses the brand.
const defaultTheme: ThemeHexColors = {
  primaryColor: '#0572d1',
  secondaryColor: '#ebf6ff'
}

const defaultAggregationTheme = {
  primaryColor: new AggregationColor(defaultTheme.primaryColor),
  secondaryColor: new AggregationColor(defaultTheme.secondaryColor)
}

const opacityString = '80'

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a CustomThemeProvider')
  }
  return context
}

export const CustomThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeAggregatedColors>(defaultAggregationTheme)
  const [workspaceLogo, setWorkspaceLogo] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchThemeColors = async () => {
      try {
        setIsLoading(true)

        const hostname = window.location.hostname
        const subdomain = hostname.split('.')[0]

        if (hostname === 'localhost') {
          return
        }

        const response = await axios.get(`workspaces/theme/${subdomain}`)
        const { primaryColor, secondaryColor, logoUrl } = response.data

        if (primaryColor && secondaryColor) {
          setThemeColors({ primaryColor, secondaryColor })
        } else {
          setThemeColors(defaultTheme)
        }

        if (logoUrl) {
          setWorkspaceLogo(logoUrl)
        } else {
          setWorkspaceLogo(null)
        }
      } catch (error) {
        setThemeColors(defaultTheme)
        setWorkspaceLogo(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchThemeColors()
  }, [])

  // OT-1249: custom Workspace colours are no longer applied anywhere. We ignore the incoming
  // colours and always apply the standardised brand palette (defaultTheme). The existing callers
  // (S3 subdomain fetch, invite, useWorkspace, UserAccessRoute) thus become no-ops for colour.
  const setThemeColors = (_colors: ThemeColors) => {
    const colors = defaultTheme
    const newTheme = {
      primaryColor: new AggregationColor(colors.primaryColor),
      secondaryColor: new AggregationColor(colors.secondaryColor)
    }
    setTheme(newTheme)
    changeRootColor('--colorPrimary', colors.primaryColor)
    changeRootColor('--colorSecondary', colors.secondaryColor)
    changeRootColor('--colorSecondaryWithOpacity', `${colors.secondaryColor}${opacityString}`)
  }

  const changeRootColor = (property: string = '--colorPrimary', newColor: string) => {
    document.documentElement.style.setProperty(property, newColor)
  }

  return (
    <ThemeContext.Provider
      value={{
        theme,
        themeColors: {
          primaryColor: theme.primaryColor.toHexString(),
          secondaryColor: theme.secondaryColor.toHexString()
        },
        setThemeColors,
        setTheme,
        workspaceLogo,
        setWorkspaceLogo,
        isLoading
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}
